import React, { useRef, ReactNode } from 'react'
import { motion, useInView, useScroll, useTransform, Variant, Variants } from 'framer-motion'
import images from '../../app/utlis/image'
import Heading from '../atoms/Heading/Heading'
import ReliabilityServiceSteps from '../molecules/ServicePageComponents/ReliabilityServiceSteps/ReliabilityServiceSteps'
import ServiceIdeaProjects from '../molecules/ServicePageComponents/ServiceIdeaProjects/ServiceIdeaProjects'
import AppsRunning from '../molecules/AppsSupportComponents/AppsRunning/AppsRunning'
import ProjectDisplay from '../molecules/ProjectDisplay/ProjectDisplay'
import AppsFaq from '../molecules/AppsFaq/AppsFaq'
import CustomService from '../molecules/ServicePageComponents/CustomService/CustomService'
import ServiceList from '../molecules/ServicePageComponents/ServiceList/ServiceList'
import DefaultFooter from '../organisms/DefaultFooter/DefaultFooter'
import { getPageData, useData } from '../../app/helper/functions'

// Animation variants
const animations: Record<string, Variants> = {
    fadeIn: {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { duration: 0.8, ease: "easeOut" }
        }
    },
    slideUp: {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1.0] }
        }
    },
    slideFromLeft: {
        hidden: { x: -100, opacity: 0 },
        visible: {
            x: 0,
            opacity: 1,
            transition: { duration: 0.7, ease: "easeOut" }
        }
    },
    slideFromRight: {
        hidden: { x: 100, opacity: 0 },
        visible: {
            x: 0,
            opacity: 1,
            transition: { duration: 0.7, ease: "easeOut" }
        }
    },
    scaleUp: {
        hidden: { scale: 0.8, opacity: 0 },
        visible: {
            scale: 1,
            opacity: 1,
            transition: { duration: 0.6, ease: [0.175, 0.885, 0.32, 1.275] }
        }
    },
    staggerContainer: {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2,
                duration: 0.4
            }
        }
    }
};

// Define MarginValue type to match what framer-motion's useInView accepts
type MarginValue = string | number;

interface ScrollAnimationOptions {
    threshold?: number;
    margin?: MarginValue;
}

// Custom hook for scroll-triggered animations
const useScrollAnimation = (options: ScrollAnimationOptions = { threshold: 0.25, margin: "-100px 0px" }) => {
    const ref = useRef(null);
    const inView = useInView(ref, {
        once: false,
        amount: options.threshold,
    });

    return { ref, inView };
};

interface AnimatedSectionProps {
    children: ReactNode;
    animation?: keyof typeof animations;
    threshold?: number;
    margin?: MarginValue;
    className?: string;
}

// Animated component wrapper
const AnimatedSection = ({
    children,
    animation = "fadeIn",
    threshold = 0.25,
    margin = "-100px 0px",
    className = ""
}: AnimatedSectionProps) => {
    const { ref, inView } = useScrollAnimation({ threshold, margin });
    const variant = animations[animation];

    return (
        <motion.div
            ref={ref}
            variants={variant}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className={className}
        >
            {children}
        </motion.div>
    );
};

const ServicePage = () => {
    const { loading, data, error } = useData();
    const customService = getPageData(data, 'customServicePage');

    // Main container ref for scroll animations
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    // Hero section ref
    const heroSectionRef = useRef(null);

    // Transform values for parallax effects
    const headerOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.8]);
    const headerY = useTransform(scrollYProgress, [0, 0.1], [0, -50]);

    return (
        <motion.div
            ref={containerRef}
            initial="hidden"
            animate="visible"
            variants={animations.fadeIn}
            className='overflow-x-hidden'
        >
            <motion.div
                className='bg-service-bg bg-center bg-cover bg-no-repeat'
                ref={heroSectionRef}
                style={{ opacity: headerOpacity }}
            >
                <div className='bg-blackTransparent'>
                    <motion.div
                        className='flex flex-col px-4 md:px-[10%] xl:px-[15%] 2xl:px-[20%] pb-8 pt-24 md:pt-25 2xl:pt-52'
                        style={{ y: headerY }}
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 1,
                            ease: [0.25, 0.1, 0.25, 1.0]
                        }}
                    >
                        <motion.img
                            src={images.SERVICEBANNERIMAGE}
                            className='z-10 w-full max-w-[90%] md:max-w-[100%] mx-auto'
                            initial={{ scale: 0.85, opacity: 0, rotate: -2 }}
                            animate={{ scale: 1, opacity: 1, rotate: 0 }}
                            transition={{
                                duration: 0.8,
                                delay: 0.2,
                                ease: [0.175, 0.885, 0.32, 1.275]
                            }}
                        />
                        <motion.div
                            className='text-white flex flex-col gap-2 relative text-center'
                            variants={animations.staggerContainer}
                            initial="hidden"
                            animate="visible"
                        >
                            <motion.div
                                className='font-bold text-3xl sm:text-5xl md:text-6xl xl:text-7xl mb-2 md:mb-4 -mt-4 md:-mt-8'
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: {
                                        opacity: 1,
                                        y: 0,
                                        transition: { duration: 0.7, ease: "easeOut" }
                                    }
                                }}
                            >
                                {customService.title}
                            </motion.div>
                            <motion.div
                                className='font-normal text-base sm:text-xl md:text-2xl px-2'
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: {
                                        opacity: 1,
                                        y: 0,
                                        transition: { duration: 0.7, delay: 0.2, ease: "easeOut" }
                                    }
                                }}
                            >
                                {customService.description}
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.div>
            
            <div className='bg-darkGreyPrimary'>
                <AnimatedSection animation="slideFromLeft">
                    <Heading className='bg-darkGreyPrimary py-8 md:py-12 px-4 md:px-6'
                        subTitle='Why choose custom software development?'
                        text="Standard software often fails to meet the specific requirements of businesses with unique needs. Custom software development offers flexibility, scalability, and full control over your system, ensuring it can grow alongside your business and adapt to changing needs." />
                </AnimatedSection>

                <AnimatedSection animation="scaleUp">
                    <ReliabilityServiceSteps customService={customService} />
                </AnimatedSection>

                <AnimatedSection animation="slideFromRight">
                    <Heading className='bg-darkGreyPrimary py-8 md:py-12 px-4 md:px-6'
                        subTitle='Our approach to custom software development'
                        text="We follow a structured and agile approach to custom software development, ensuring that your solution is delivered on time, within budget, and meets all your requirements." />
                </AnimatedSection>

                <AnimatedSection animation="slideUp" threshold={0.1}>
                    <ServiceIdeaProjects customService={customService} />
                </AnimatedSection>

                <AnimatedSection animation="slideFromLeft" className="py-6 md:py-10 bg-darkGreyPrimary ">
                    <AppsRunning
                        bgImage='bg-confident-center-bg'
                        title='Key features of our custom software solutions'
                        subtitle='Our custom software solutions are designed to deliver measurable results & drive your business forward. Some of the most common features we build include'
                    />
                </AnimatedSection>

                <AnimatedSection animation="staggerContainer">
                    <ServiceList customService={customService} />
                </AnimatedSection>

                <AnimatedSection animation="slideFromRight" threshold={0.1}>
                    <ProjectDisplay />
                </AnimatedSection>

                <AnimatedSection animation="slideUp" threshold={0.1}>
                    <AppsFaq />
                </AnimatedSection>

                <AnimatedSection animation="scaleUp">
                    <CustomService />
                </AnimatedSection>

                <AnimatedSection>
                    <DefaultFooter />
                </AnimatedSection>
            </div>
        </motion.div>
    )
}

export default ServicePage
